/** @format */

import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, TextField, Select, FormControl, InputLabel, MenuItem, Checkbox } from '@material-ui/core';
import { Button } from 'src/components';
// import {ArrowRight} from '@material-ui/icons';
import { Avatar } from 'src/components/header/avatar';
import useAuth from 'src/hooks/useAuth';
import { useHistory } from 'react-router-dom';
import { useEffect } from 'react';
import {
  deleteUserProfilePicture,
  setUserData,
  updateUserInfo,
  updateUserProfilePicture,
  uploadUserProfilePicture
} from 'src/slices/users';
import { useDispatch } from 'src/store';
import Breadcrumb from 'src/components/Breadcrumb';
import { countriesMap } from 'src/utils/dataMap';
import { useSelector } from 'src/store';
import prettyBytes from 'pretty-bytes';

const salutationValues = [
  { id: 'mr', label: 'Mr.' },
  { id: 'mrs', label: 'Mrs.' },
  { id: 'others', label: 'Others' }
];

const useStyles = makeStyles(theme => ({
  root: {
    fontSize: '1.5rem',
    margin: '0 auto'
  },
  card: {
    padding: '2.4rem 1.6rem',
    margin: '2.4rem 0 0',
    background: '#f1f2f4',
    fontSize: '16px',
    color: '#333',
    position: 'relative',
    borderRadius: '1.6rem',
    boxShadow: 'none',
    gap: '3.2rem',
    [theme.breakpoints.up('md')]: {
      padding: '4rem ',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'flex-start'
    }
  },
  textColor: {
    color: '#999',
    lineHeight: '2.5rem',
    paddingTop: '1rem',
    fontSize: '14px'
  },
  btnwrapper: {
    marginTop: 15
  },
  linkcolor: {
    color: '#08C476',
    fontSize: '3.5rem',
    verticalAlign: 'middle'
  },
  checkbox: {
    color: '#333',
    fontSize: '3.5rem',
    verticalAlign: 'middle',
    '& .MuiSvgIcon-root': { fontSize: 28 },
    '&.MuiCheckbox-colorSecondary.Mui-checked': {
      color: '#333'
    }
  },
  btnStyle: {
    border: '2px solid #333',
    borderRadius: 5,
    fontSize: '1.2rem',
    color: '#FFF',
    fontWeight: '700',
    background: '#000',
    padding: 10,
    '&:hover': {
      background: 'transparent',
      border: '2px solid #333',
      color: '#333'
    }
  },
  delbtnStyle: {
    marginLeft: '1.5rem',
    borderRadius: 25,
    fontSize: '1.2rem',
    color: '#333',
    fontWeight: '700',
    '&:hover': {
      background: 'transparent'
    }
  },
  textField: {
    marginRight: theme.spacing(1),
    '& > *': {
      fontSize: '1.5rem',
      color: '#333'
    },
    '& > .MuiInput-underline': {
      '&:before': {
        borderBottom: '2px solid #fea500'
      }
    },
    '& > .MuiInputLabel-formControl': {
      color: '#fea500'
    }
  },
  footerBtn: {
    marginTop: 25,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 16
  },
  abortBtn: {
    marginLeft: '1.5rem',
    borderRadius: 25,
    fontSize: '1.2rem',
    color: '#333',
    fontWeight: '700',
    '&:hover': {
      background: 'transparent'
    }
  },
  formControl: {
    marginTop: theme.spacing(2),
    minWidth: '100%',
    '& > *': {
      fontSize: '1.5rem',
      color: '#333'
    },
    '& > .MuiInput-underline': {
      '&:before': {
        borderBottom: '2px solid #fea500'
      }
    },
    '& > .MuiInputLabel-formControl': {
      color: '#fea500'
    }
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
    fontSize: '1.5rem'
  },
  cardHead: {
    width: 'auto',
    maxWidth: '693px',
    flex: 'auto'
  },
  profileImg: {
    background: 'transparent',
    borderRadius: 15,
    alignItems: 'center',
    gap: '1.6rem',
    [theme.breakpoints.up('md')]: {
      gap: '4rem',
      width: 'auto'
    }
  },
  profileImgBtns: {
    marginTop: '1rem',
    background: 'transparent',
    borderRadius: 15,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0.8rem',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: '1.6rem'
    }
  },
  hidden: {
    display: 'none'
  }
}));

const PersonalData = props => {
  const { user } = useAuth();
  const classes = useStyles();
  let history = useHistory();
  const dispatch = useDispatch();
  const { error, userProfile, userLoading, userData } = useSelector(state => state.users);
  const [errorMessage, setErrorMessage] = useState({ error: error });
  const [userProfilePicture, setUserProfilePicture] = useState('');
  const { t, i18n } = useTranslation();
  const inputFileRef = useRef();
  const [successMessage, setSuccessMessage] = useState(null);

  const goBack = () => {
    history.push('/profile');
  };

  useEffect(() => {
    if (userProfile && userProfile.picture && !userProfilePicture) {
      setUserProfilePicture(userProfile.picture);
    }
    // eslint-disable-next-line
  }, [userProfile]);

  useEffect(() => {
    if (error) {
      const errorMsg = error.error
        .replace('The mime type of the file is invalid', t('personalData.profile_errors.mime_1'))
        .replace('Allowed mime types are', t('personalData.profile_errors.mime_2'))
        .replace('An image for this user already exists.', t('personalData.profile_errors.already_exists'))
        .replace('Unable to save image.', t('personalData.profile_errors.unable_save'))
        .replace('No image uploaded.', t('personalData.profile_errors.no_image_uploaded'))
        .replace('The file was only partially uploaded.', t('personalData.profile_errors.was_partially_uploaded'))
        .replace('No file was uploaded.', t('personalData.profile_errors.no_file_uploaded'))
        .replace('The file could not be uploaded.', t('personalData.profile_errors.file_could_not_be_uploaded'));
      setErrorMessage({ ...errorMessage, avatar: errorMsg });
      setUserProfilePicture('');
      inputFileRef.current.value = null;
    }
    // eslint-disable-next-line
  }, [error]);

  const setData = e => {
    const { name, value } = e.target;
    dispatch(
      setUserData({
        ...userData,
        [name]: value
      })
    );
  };
  const setCheck = e => {
    const { name, checked } = e.target;
    dispatch(
      setUserData({
        ...userData,
        [name]: checked
      })
    );
  };
  const setAddress = e => {
    const { name, value } = e.target;
    let address = { ...userData.address, ...{ [name]: value } };
    dispatch(
      setUserData({
        ...userData,
        address
      })
    );
  };
  const saveProfileData = async () => {
    const { firstName, lastName, company, address } = userData;
    setErrorMessage({
      ...errorMessage,
      firstName: firstName ? null : t('personalData.errorMessage.firstName'),
      lastName: lastName ? null : t('personalData.errorMessage.lastName'),
      company: company ? null : t('personalData.errorMessage.company'),
      phoneNumber: address.phoneNumber ? null : t('personalData.errorMessage.phoneNumber'),
      address: address.street && address.postalCode && address.city ? null : t('personalData.errorMessage.address')
    });
    if (
      firstName &&
      lastName &&
      company &&
      address.street &&
      address.postalCode &&
      address.city &&
      address.phoneNumber
    ) {
      setErrorMessage({ error: null });
      setSuccessMessage(null);
      await dispatch(updateUserInfo(userData));
      setSuccessMessage(t('personalData.successMessage'));
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    } else {
      setSuccessMessage(null);
    }
  };

  const breadcrumbData = [
    {
      title: t('profile.title'),
      link: '/profile',
      options: [
        {
          title: t('profile.title'),
          link: '/profile'
        },
        {
          title: t('personalData.title'),
          link: '/personaldata'
        },
        {
          title: t('Access.title'),
          link: '/access'
        }
      ]
    },
    {
      title: t('personalData.title')
    }
  ];

  const onChangeProFilePicture = async e => {
    setSuccessMessage(null);
    try {
      const [file] = e.target.files;
      if (file) {
        const { size } = file;
        if (size / (1024 * 1024) > 2) {
          const sizePretty = prettyBytes(size);
          setErrorMessage({
            ...errorMessage,
            avatar: t('personalData.profile_errors.maxSize', { fileSize: sizePretty, limit: '2 MB' })
          });
          return;
        }

        const picture = URL.createObjectURL(file);
        setUserProfilePicture(picture);
        e.target.value = null;

        if (userProfilePicture) await dispatch(deleteUserProfilePicture());

        const data = new FormData();
        data.append('file', file);

        await dispatch(uploadUserProfilePicture({ data }));
        setSuccessMessage(t('personalData.successMessage'));
        setErrorMessage({
          ...errorMessage,
          avatar: ''
        });
        await dispatch(updateUserProfilePicture());
      }
    } catch (error) {
      setErrorMessage({
        ...errorMessage,
        avatar: 'Something went wrong!'
      });
    }
  };

  const deleteUserProfile = async () => {
    await dispatch(deleteUserProfilePicture());
    inputFileRef.current.value = null;
    setUserProfilePicture('');
  };

  const checkErrorMessage = () => {
    return Object.keys(errorMessage).some(key => {
      return errorMessage[key] !== null && errorMessage[key] !== '';
    });
  };

  const role = useMemo(() => {
    if (user && Object.keys(user).length > 0) {
      const { scenario, role, roleOther, roles } = user;
      if (roleOther) return roleOther;
      if (role) return t(`personalData.roles.${role}`);
      if (scenario) return t(`personalData.scenario.${scenario}`);
      if (roles && roles.length > 0) return roles[0];
      return 'Fleet Manager';
    }
    return '';
  }, [user, t]);

  return (
    <section className="main_content profile">
      <Breadcrumb data={breadcrumbData} />
      <div className=" container">
        {checkErrorMessage(errorMessage) ? (
          <div className="errorMessage">
            <span>{t('personalData.errorMessage.overall')}</span>
          </div>
        ) : (
          successMessage && (
            <div className="successMessage">
              <span>{successMessage}</span>
            </div>
          )
        )}
        {/* <h4 className="page_subtitle">{t('profile.subTitle')}</h4> */}
        <div className="personal-data-container">
          <h2 className="page_title">{t('personalData.title')}</h2>
          {/* Profile Picture */}
          <Grid container className={classes.card}>
            <h4 className="sub_head">{t('personalData.profilepic')}</h4>
            <Grid container className={classes.profileImg}>
              <Grid className="profile_pic">
                <Avatar
                  loading={userLoading || userProfile.pictureLoading}
                  srcImg={userProfilePicture}
                  name={`${userData?.firstName} ${userData?.lastName}`}
                />
              </Grid>
              <Grid className="profile_pic_right">
                <Grid className="profile-picture-name">profile.png</Grid>
                <Grid container className={classes.profileImgBtns}>
                  <Grid item sx={6} style={{ display: 'flex', alignItems: 'center' }}>
                    {userProfilePicture && (
                      <Button
                        disabled={userLoading}
                        label={t('personalData.deleteBtn')}
                        className={`orange-button delete-btn delete-button--${i18n.language}`}
                        onClick={deleteUserProfile}
                      />
                    )}
                  </Grid>
                  <Grid item sx={6} style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      ref={inputFileRef}
                      type="file"
                      accept=".jpg, .jpeg, .png"
                      disabled={userLoading}
                      onChange={onChangeProFilePicture}
                      id="profilePictureUploadBtn"
                      style={{ display: 'none' }}
                    />
                    <label
                      htmlFor="profilePictureUploadBtn"
                      className={`orange-button upload-btn upload-btn--${i18n.language}`}>
                      {t('personalData.uploadBtn')}
                    </label>
                  </Grid>
                </Grid>
                {errorMessage.avatar && <Grid className="page_error">{errorMessage.avatar}</Grid>}
              </Grid>
            </Grid>
          </Grid>
          {/* Profile Data */}
          <Grid className={classes.card}>
            <h4 className="sub_head">{t('personalData.title')}</h4>
            <Grid className={classes.cardHead}>
              <Grid>
                <FormControl className={classes.formControl}>
                  <InputLabel id="salutation">{t('personalData.salutation')}</InputLabel>
                  <Select
                    labelId="salutation"
                    name="salutation"
                    id="salutation"
                    value={userData?.salutation || ''}
                    onChange={setData}>
                    {salutationValues.map(s => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid>
                <TextField
                  style={{ backgroundColor: errorMessage.firstName ? '#ffe0e1' : '' }}
                  id="standard-full-width"
                  label={t('personalData.firstName')}
                  placeholder={t('personalData.firstName')}
                  value={userData?.firstName || ''}
                  fullWidth
                  className={classes.textField}
                  onChange={setData}
                  name="firstName"
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
                {errorMessage.firstName && <div className="errorMessageInput">{errorMessage.firstName}</div>}
              </Grid>
              <Grid>
                <TextField
                  style={{ backgroundColor: errorMessage.lastName ? '#ffe0e1' : '' }}
                  id="standard-full-width"
                  label={t('personalData.Surname')}
                  placeholder={t('personalData.Surname')}
                  fullWidth
                  value={userData?.lastName || ''}
                  className={classes.textField}
                  onChange={setData}
                  name="lastName"
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
                {errorMessage.lastName && <div className="errorMessageInput">{errorMessage.lastName}</div>}
              </Grid>
              <Grid>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.position')}
                  placeholder={t('personalData.position')}
                  fullWidth
                  value={role}
                  className={classes.textField}
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid>
                <TextField
                  style={{ backgroundColor: errorMessage.company ? '#ffe0e1' : '' }}
                  id="standard-full-width"
                  label={t('personalData.company')}
                  placeholder={t('personalData.company')}
                  fullWidth
                  className={classes.textField}
                  margin="normal"
                  value={userData?.company || ''}
                  onChange={setData}
                  name="company"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
                {errorMessage.company && <div className="errorMessageInput">{errorMessage.company}</div>}
              </Grid>
            </Grid>
          </Grid>
          {/* Company Address */}
          <Grid className={classes.card}>
            <h4 className="sub_head">{t('personalData.companyaddress')}</h4>
            <Grid container className={classes.cardHead}>
              <Grid item xs={12}>
                <TextField
                  style={{ backgroundColor: errorMessage.address ? '#ffe0e1' : '' }}
                  id="standard-full-width"
                  label={t('personalData.Street')}
                  placeholder={t('personalData.Street')}
                  fullWidth
                  className={classes.textField}
                  margin="normal"
                  value={(userData && userData.address)?.street || ''}
                  onChange={setAddress}
                  name="street"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    style={{ backgroundColor: errorMessage.address ? '#ffe0e1' : '' }}
                    id="standard-full-width"
                    label={t('personalData.Postcode')}
                    placeholder={t('personalData.Postcode')}
                    className={classes.textField}
                    value={(userData && userData.address)?.postalCode || ''}
                    onChange={setAddress}
                    name="postalCode"
                    margin="normal"
                    fullWidth
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    style={{ backgroundColor: errorMessage.address ? '#ffe0e1' : '' }}
                    id="standard-full-width"
                    label={t('personalData.city')}
                    placeholder={t('personalData.city')}
                    className={classes.textField}
                    margin="normal"
                    fullWidth
                    value={(userData && userData.address)?.city || ''}
                    onChange={setAddress}
                    name="city"
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    id="standard-full-width"
                    label={t('personalData.country')}
                    placeholder={t('personalData.country')}
                    className={classes.textField}
                    margin="normal"
                    fullWidth
                    value={userData?.address?.country ? countriesMap.get(userData.address.country) : ''}
                    onChange={setAddress}
                    name="country"
                    InputLabelProps={{
                      shrink: true
                    }}
                    inputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  {errorMessage.address && <div className="errorMessageInput">{errorMessage.address}</div>}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {/* Contact */}
          <Grid className={classes.card}>
            <h4 className="sub_head">{t('personalData.Contact_Information')}</h4>
            <Grid className={classes.cardHead}>
              <TextField
                style={{ backgroundColor: errorMessage.phoneNumber ? '#ffe0e1' : '' }}
                id="standard-full-width"
                label={t('personalData.phone')}
                placeholder={t('personalData.phone')}
                className={classes.textField}
                value={(userData && userData.address)?.phoneNumber || ''}
                onChange={setAddress}
                name="phoneNumber"
                margin="normal"
                fullWidth
                InputLabelProps={{
                  shrink: true
                }}
              />
              <TextField
                id="standard-full-width"
                label={t('personalData.Faxnumber')}
                placeholder={t('personalData.Faxnumber')}
                className={classes.textField}
                value={(userData && userData.address)?.faxNumber || ''}
                onChange={setAddress}
                name="faxNumber"
                margin="normal"
                fullWidth
                InputLabelProps={{
                  shrink: true
                }}
              />
              {errorMessage.phoneNumber && <Grid className="errorMessageInput">{errorMessage.phoneNumber}</Grid>}
            </Grid>
          </Grid>
          {/* Address */}
          <Grid className={classes.card}>
            <h4 className="sub_head">{t('personalData.Additional_Information')}</h4>
            <Grid className={classes.cardHead}>
              <Grid>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.VAT_Number')}
                  placeholder={t('personalData.VAT_Number')}
                  fullWidth
                  value={userData?.vatNumber || ''}
                  onChange={setData}
                  name="vatNumber"
                  className={classes.textField}
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.TCO_Number')}
                  placeholder={t('personalData.TCO_Number')}
                  fullWidth
                  value={userData?.sealNumber || ''}
                  onChange={setData}
                  name="sealNumber"
                  className={classes.textField}
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.Diesel_repair_service_id')}
                  placeholder={t('personalData.Diesel_repair_service_id')}
                  fullWidth
                  className={classes.textField}
                  value={userData?.drsId || ''}
                  onChange={setData}
                  name="drsId"
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.ATE_Break_Center_ID')}
                  placeholder={t('personalData.ATE_Break_Center_ID')}
                  fullWidth
                  value={userData?.brakeCenterId || ''}
                  onChange={setData}
                  name="brakeCenterId"
                  className={classes.textField}
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid>
                <TextField
                  id="standard-full-width"
                  label={t('personalData.VDO_Fleet_Dealer_ID')}
                  placeholder={t('personalData.VDO_Fleet_Dealer_ID')}
                  fullWidth
                  value={userData?.dealerId || ''}
                  onChange={setData}
                  name="dealerId"
                  className={classes.textField}
                  margin="normal"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid className={classes.card}>
            <h4 className="sub_head">Newsletter</h4>
            <Grid className={classes.cardHead}>
              <Checkbox
                id="newsletter"
                className={classes.checkbox}
                name="newsletter"
                checked={userData && userData.hasOwnProperty('newsletter') ? userData.newsletter : false}
                onChange={setCheck}
              />{' '}
              <label htmlFor="newsletter" className="checkbox-text">
                {t('personalData.newsletter')}
              </label>
            </Grid>
          </Grid>
          <Grid>
            <div className={classes.footerBtn}>
              <Button onClick={goBack} ghost noArrow label={t('personalData.Abortbtn')} />
              <Button onClick={saveProfileData} label={t('personalData.saveBtn')} />
            </div>
          </Grid>
        </div>
      </div>
    </section>
  );
};

export { PersonalData };
